import { z } from "zod"

import { difficultySchema } from "./schemas"

/**
 * Die Verträge der Prüfungsvorbereitung.
 *
 * Eigene Datei statt eines Blocks in `schemas.ts`, weil hier drei Ebenen
 * nebeneinander stehen, die man nicht verwechseln darf: der Katalog (mit der
 * Lösung), die Karte (ohne die Lösung) und die Auswertung (mit der Lösung,
 * aber erst nach der Antwort).
 *
 * Die Trennung ist der Kern der Halluzinationssicherheit: was das Modell zu
 * sehen bekommt, ist `pruefungsFrageKarteSchema` — dort gibt es kein Feld, in
 * dem die richtige Option stehen könnte.
 */

export const pruefungsFrageTypSchema = z.enum([
  "single",
  "multi",
  "wahrfalsch",
  "offen",
])
export type PruefungsFrageTyp = z.infer<typeof pruefungsFrageTypSchema>

export const PRUEFUNGS_FRAGE_TYP_LABELS: Record<PruefungsFrageTyp, string> = {
  single: "Einfachauswahl",
  multi: "Mehrfachauswahl",
  wahrfalsch: "Wahr oder falsch",
  offen: "Offene Frage",
}

/** Eine Antwortoption, wie sie im Katalog steht — mit der Lösung. */
export const pruefungsOptionSchema = z.object({
  key: z.string().min(1).max(8),
  text: z.string().min(1),
  richtig: z.boolean(),
})
export const pruefungsOptionenSchema = z.array(pruefungsOptionSchema)
export type PruefungsOption = z.infer<typeof pruefungsOptionSchema>

/** Dieselbe Option, wie sie Modell und Oberfläche sehen — ohne die Lösung. */
export const pruefungsKartenOptionSchema = pruefungsOptionSchema.omit({
  richtig: true,
})
export type PruefungsKartenOption = z.infer<typeof pruefungsKartenOptionSchema>

/**
 * Was `naechsteFrage` zurückgibt.
 *
 * Bewusst ohne `richtig`, ohne `musterantwort` und ohne `begruendung`: das
 * Modell soll die Frage stellen können und sonst nichts. Alles, was die Lösung
 * verrät, kommt erst mit der Auswertung — und die entsteht auf dem Server.
 */
export const pruefungsFrageKarteSchema = z.object({
  frageId: z.string(),
  key: z.string(),
  typ: pruefungsFrageTypSchema,
  lernfeldSlug: z.string(),
  lernfeldTitel: z.string(),
  stamm: z.string(),
  optionen: z.array(pruefungsKartenOptionSchema),
  schwierigkeit: difficultySchema,
  /**
   * Position within this thread's run, "Frage 3 von 15". Frozen into the tool
   * output, so a card keeps its number after a reload instead of recounting.
   * Optional because cards stored before this field existed lack it.
   */
  nummer: z.number().int().positive().optional(),
  gesamt: z.number().int().positive().optional(),
})
export type PruefungsFrageKarte = z.infer<typeof pruefungsFrageKarteSchema>

/** Ab dieser Quote in Prozent gilt eine Schicht im Durchgang als bestanden. */
export const PRUEFUNG_BESTEHENSGRENZE = 70

/** Die Rundengrößen, die zum Start angeboten werden; dazu kommt „Alle“. */
export const PRUEFUNG_ZIEL_VORSCHLAEGE = [5, 10, 20] as const
export const PRUEFUNG_ZIEL_STANDARD = 10
/** Um so viele Fragen lässt sich ein erreichtes Ziel verlängern. */
export const PRUEFUNG_ZIEL_VERLAENGERUNGEN = [5, 10] as const
export const PRUEFUNG_ZIEL_MAX = 200

/** Das Lernziel eines Gesprächs: so viele Fragen will sie beantworten. */
export const pruefungsZielSchema = z.object({
  ziel: z.number().int().min(1).max(PRUEFUNG_ZIEL_MAX),
})
export type PruefungsZiel = z.infer<typeof pruefungsZielSchema>

/**
 * Relative on purpose: an older result card further up the thread would
 * otherwise send a stale absolute target and undo a later extension.
 */
export const pruefungsZielVerlaengerungSchema = z.object({
  anzahl: z.number().int().min(1).max(50),
})

/** Wie das Lernziel gelaufen ist, summiert über alle Schichten. */
export const pruefungsZielErgebnisSchema = z.object({
  anzahl: z.number().int(),
  beantwortet: z.number().int(),
  richtig: z.number().int(),
  /** Trefferquote in Prozent; null ohne Antwort. */
  quote: z.number().int().nullable(),
  /**
   * Quote auf Prüfungsniveau. Bewusst nicht „bestanden“: eine Teilrunde fragt
   * die Schicht nicht vollständig ab.
   */
  erreicht: z.boolean(),
  /** Ob der Bereich noch Fragen hat; ohne sie gibt es nichts zu verlängern. */
  weitereFragen: z.boolean(),
})
export type PruefungsZielErgebnis = z.infer<typeof pruefungsZielErgebnisSchema>

/** Eine Schicht, wie sie in diesem Gespräch gelaufen ist. */
export const pruefungsAbschlussSchichtSchema = z.object({
  slug: z.string(),
  titel: z.string(),
  beantwortet: z.number().int(),
  richtig: z.number().int(),
  gesamt: z.number().int(),
  /** Trefferquote in Prozent; null ohne Antwort in diesem Gespräch. */
  quote: z.number().int().nullable(),
  /** Null, solange die Schicht in diesem Gespräch nicht fertig ist. */
  bestanden: z.boolean().nullable(),
})
export type PruefungsAbschlussSchicht = z.infer<
  typeof pruefungsAbschlussSchichtSchema
>

/**
 * Das Ergebnis, sobald eine Schicht oder der ganze Katalog im Gespräch durch
 * ist. Gerechnet über dieses Gespräch, nicht über den Lernstand: jede Frage
 * kommt hier nur einmal, die Quote ist also die der Simulation.
 */
export const pruefungsAbschlussSchema = z.object({
  /** Die gerade beendete Schicht; fehlt, wenn gemischt alles durch ist. */
  lernfeldSlug: z.string().optional(),
  grenze: z.number().int(),
  schichten: z.array(pruefungsAbschlussSchichtSchema),
  /** Only when the thread has a question target. */
  ziel: pruefungsZielErgebnisSchema.optional(),
})
export type PruefungsAbschluss = z.infer<typeof pruefungsAbschlussSchema>

/** Wenn der Katalog nichts hergibt, sagt das Werkzeug das, statt zu erfinden. */
export const pruefungsKeineFrageSchema = z.object({
  keineFrage: z.literal(true),
  grund: z.string(),
  /** Optional because outputs stored before this field existed lack it. */
  abschluss: pruefungsAbschlussSchema.optional(),
})

export const naechsteFrageErgebnisSchema = z.union([
  pruefungsFrageKarteSchema,
  pruefungsKeineFrageSchema,
])
export type NaechsteFrageErgebnis = z.infer<typeof naechsteFrageErgebnisSchema>

/**
 * Die abgegebene Antwort.
 *
 * `toolCallId` verankert sie am Werkzeugaufruf, nicht an der Frage: dieselbe
 * Frage kann in einem Gespräch zweimal drankommen, und nach dem Neuladen muss
 * jede Karte ihren eigenen Zustand wiederfinden.
 */
export const pruefungsAntwortRequestSchema = z.object({
  frageId: z.string().min(1),
  toolCallId: z.string().min(1).optional(),
  threadId: z.string().min(1).optional(),
  /** Optionsschlüssel bei den Auswahltypen, leer lassen bei `offen`. */
  gewaehlt: z.array(z.string().min(1)).max(20).default([]),
  /** Der Freitext bei `offen`. */
  text: z.string().max(4000).optional(),
})
export type PruefungsAntwortRequest = z.infer<
  typeof pruefungsAntwortRequestSchema
>

/**
 * Das Ergebnis der Bewertung. Entsteht ausschließlich auf dem Server; die
 * Karte zeigt es sofort, ohne auf den Coach zu warten.
 */
export const pruefungsAuswertungSchema = z.object({
  antwortId: z.string(),
  frageId: z.string(),
  toolCallId: z.string().optional(),
  typ: pruefungsFrageTypSchema,
  richtig: z.boolean(),
  /** Die Schlüssel der richtigen Optionen; bei `offen` leer. */
  richtigeOptionen: z.array(z.string()),
  gewaehlt: z.array(z.string()),
  text: z.string().optional(),
  /** Wörtlich aus dem Katalog. */
  begruendung: z.string(),
  quelle: z.string(),
  /** Nur bei `offen`: die Musterantwort und das Urteil des Modells. */
  musterantwort: z.string().optional(),
  bewertung: z.string().optional(),
})
export type PruefungsAuswertung = z.infer<typeof pruefungsAuswertungSchema>

/** Ein Eintrag auf dem Merkzettel des Coaches. */
export const merkzettelEintragSchema = z.object({
  lernfeldSlug: z.string(),
  text: z.string().min(3).max(300),
  at: z.string(),
})
export const merkzettelSchema = z.array(merkzettelEintragSchema)
export type MerkzettelEintrag = z.infer<typeof merkzettelEintragSchema>

/** Wie viele Beobachtungen der Coach mitschleppen darf. */
export const MERKZETTEL_MAX = 40

/** Leitner-Intervalle in Tagen, Index ist das Fach. */
export const LEITNER_INTERVALLE_TAGE = [0, 1, 3, 7, 21] as const
export const LEITNER_MAX_FACH = LEITNER_INTERVALLE_TAGE.length - 1

/** Der Stand in einem Lernfeld, aggregiert aus den Antworten. */
export const lernfeldStandSchema = z.object({
  slug: z.string(),
  titel: z.string(),
  beschreibung: z.string(),
  gewicht: z.number().int(),
  fragenGesamt: z.number().int(),
  /** Fragen, die mindestens einmal beantwortet wurden. */
  fragenGesehen: z.number().int(),
  /** Fragen, die im höchsten Leitner-Fach stehen. */
  fragenSitzen: z.number().int(),
  versuche: z.number().int(),
  richtige: z.number().int(),
  faellig: z.number().int(),
})
export type LernfeldStand = z.infer<typeof lernfeldStandSchema>

export const pruefungsUebersichtSchema = z.object({
  /** Fehlt, solange kein Katalog eingespielt ist. */
  katalog: z
    .object({
      version: z.string(),
      titel: z.string(),
      rahmen: z.string(),
    })
    .optional(),
  lernfelder: z.array(lernfeldStandSchema),
})
export type PruefungsUebersicht = z.infer<typeof pruefungsUebersichtSchema>

export const pruefungsLernstandSchema = z.object({
  pruefungAm: z.string().optional(),
  merkzettel: merkzettelSchema,
  lernfelder: z.array(lernfeldStandSchema),
  versuche: z.number().int(),
  richtige: z.number().int(),
})
export type PruefungsLernstand = z.infer<typeof pruefungsLernstandSchema>

export const pruefungsAuswertungListSchema = z.array(pruefungsAuswertungSchema)

export const updatePruefungsLernstandSchema = z.object({
  /** ISO-Datum; null löscht den Termin wieder. */
  pruefungAm: z.string().nullable().optional(),
})

/**
 * Der Stand, wie ihn die Karte im Gespräch zeigt.
 *
 * Bewusst ohne den Merkzettel: der gehört dem Coach als Gedächtnisstütze und
 * ist in Prosa formuliert („verwechselt X mit Y"). Als Aufzählung auf einer
 * Karte läse er sich wie ein Mängelbericht über die Beraterin.
 */
export const pruefungsstandKarteSchema = z.object({
  versuche: z.number().int(),
  richtige: z.number().int(),
  pruefungAm: z.string().optional(),
  lernfelder: z.array(
    z.object({
      slug: z.string(),
      titel: z.string(),
      /** Trefferquote in Prozent; null, solange nichts beantwortet wurde. */
      quote: z.number().int().nullable(),
      gesehen: z.number().int(),
      gesamt: z.number().int(),
      sitzen: z.number().int(),
    }),
  ),
})
export type PruefungsstandKarte = z.infer<typeof pruefungsstandKarteSchema>
